import { Component, type PropsWithChildren } from "react";
import { Navigate } from "react-router-dom";

interface State {
  hasError: boolean;
  /** True when we already reloaded once for this failure and it still
   *  fails — so this is a real 403 (gated chunk), not a stale bundle. */
  giveUp: boolean;
}

/** sessionStorage key: epoch ms of the last reload we triggered. */
const RELOAD_KEY = "ns:chunk-reload-at";
/** A second chunk failure inside this window is treated as "really
 *  forbidden", not "stale bundle". */
const RELOAD_WINDOW_MS = 60_000;

function recentlyReloaded(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < RELOAD_WINDOW_MS;
  } catch {
    return false;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* private mode / storage blocked — we'll just fall back to home */
  }
}

/**
 * Catches the error React.lazy throws when a route chunk fails to
 * load. Two very different causes produce the same error:
 *
 *  1. STALE BUNDLE — the tab has an old index.js from before a deploy,
 *     and asks for `/assets/admin/AdminX-<oldhash>.js` which no longer
 *     exists (404). This happens to every open tab after EVERY deploy
 *     (2026-10-17: "no admin tab opens" right after a release). The
 *     fix is simply a full page reload — the fresh index.html points at
 *     the new hashes.
 *
 *  2. GATED CHUNK — FastAPI's `/assets/admin/*` gate returns 403 to a
 *     non-admin who tampered with `/me` to render the admin shell. Here
 *     a reload would fail again, so we bounce home instead.
 *
 * Strategy: reload once (remembered in sessionStorage); if a chunk
 * fails again within a minute of that reload, it's case 2 → navigate
 * to `/`. No logging on purpose — case 2 is *expected* and we don't
 * want to spam Sentry.
 *
 * The filename deliberately doesn't start with `Admin` — the Vite
 * `manualChunks` rule treats `/pages/Admin*` as admin-only modules
 * and groups them into the gated chunk; this boundary needs to live
 * in the customer bundle (it's what catches the chunk failure for a
 * non-admin), so it's named `RouteChunkBoundary` instead.
 */
export class RouteChunkBoundary extends Component<PropsWithChildren, State> {
  state: State = { hasError: false, giveUp: false };

  static getDerivedStateFromError(): State {
    if (recentlyReloaded()) {
      return { hasError: true, giveUp: true };
    }
    markReloaded();
    return { hasError: true, giveUp: false };
  }

  componentDidUpdate(_prev: PropsWithChildren, prevState: State) {
    if (this.state.hasError && !this.state.giveUp && !prevState.hasError) {
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.state.giveUp) {
        return <Navigate to="/" replace />;
      }
      // Reload is in flight (see componentDidUpdate); show nothing
      // rather than flashing the catalog for a frame.
      return null;
    }
    return this.props.children;
  }
}
